// Phase 7 R5 — Read-only procurement event history for a PO.
//
// SOURCE OF TRUTH: `procurement_events` written by R1–R4 RPCs.
// This hook ONLY reads. It does not mutate, infer, or fabricate state.
// Display order is NEWEST FIRST (created_at DESC) — see ProcurementTimelineCard.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ProcurementEventType =
  | 'po_created'
  | 'po_line_added'
  | 'po_submitted'
  | 'qty_received'
  | 'cost_corrected'
  | 'qty_returned'
  // Forward-compatible (not emitted yet, render falls back gracefully):
  | 'return_credited'
  | 'return_rejected'
  | string;

export interface ProcurementEvent {
  id: string;
  shop_id: string;
  event_type: ProcurementEventType;
  purchase_order_id: string | null;
  purchase_order_line_id: string | null;
  parts_line_id: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

export function useProcurementTimeline(poId: string | undefined) {
  return useQuery({
    queryKey: ['procurement-timeline', poId],
    enabled: !!poId,
    queryFn: async (): Promise<ProcurementEvent[]> => {
      const { data: events, error } = await (supabase as any)
        .from('procurement_events')
        .select('*')
        .eq('purchase_order_id', poId)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const rows = (events ?? []) as Array<Omit<ProcurementEvent, 'actor_name'>>;
      const actorIds = Array.from(
        new Set(rows.map((r) => r.actor_user_id).filter((x): x is string => !!x)),
      );

      let nameById = new Map<string, string>();
      if (actorIds.length > 0) {
        const { data: profiles } = await (supabase as any)
          .from('profiles')
          .select('id, full_name, email')
          .in('id', actorIds);
        nameById = new Map(
          (profiles ?? []).map((p: any) => [
            p.id as string,
            (p.full_name as string | null) || (p.email as string | null) || 'Unknown user',
          ]),
        );
      }

      return rows.map((r) => ({
        ...r,
        actor_name: r.actor_user_id ? nameById.get(r.actor_user_id) ?? 'Unknown user' : 'System',
      }));
    },
  });
}
